import { NoData } from "@/components/common/section-components/NoData";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { EstablishmentFieldRequestFilter } from "@/components/common/section-components/filters/EstablishmentFieldRequestFilter";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { EstablishmentFieldRequestStatus } from "@/constants/entity-status";
import { RequestTypes } from "@/constants/enums";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { EstablishmentFieldRequestItem, EstablishmentFieldRequestsListResponse, GetEstablishmentFieldRequestsProps } from "@/services/establishment-field-request.service";
import { Flex } from "@chakra-ui/react";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  clearEstablishmentFieldRequestFiltersHandler,
  fetchEstablishmentFieldRequestsHandler,
  submitEstablishmentFieldRequestFiltersHandler,
} from "./schema/handler-functions";
import { establishmentFieldRequestsHeaderElements } from "./schema/header-elements";
import { EstablishmentFieldRequestActions } from "@/components/common/section-components/action/EstablishmentFieldRequestActions";
import { isEmpty } from "lodash";
import { usePolling } from "@/hooks/usePolling";

export const EstablishmentsFieldsRequestsPage = () => {
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<EstablishmentFieldRequestsListResponse | undefined>();
  const [page, setPage] = useState(1);
  const [openFilters, setOpenFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<Partial<GetEstablishmentFieldRequestsProps> | undefined>();

  const { control, handleSubmit, setValue, getValues } = useForm();

  useEffect(() => {
    setIsActive(true);
  }, []);

  const fetchList = async (filters?: any, showLoad = true) => {
    await fetchEstablishmentFieldRequestsHandler({
      filters,
      showLoad,
      setLoading,
      setList,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (isActive) fetchList({ "P.Pagina": 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const submitFilters = async (event: FieldValues) => {
    setPage(1);
    await submitEstablishmentFieldRequestFiltersHandler({
      event,
      setOpenFilters,
      setFiltersApplied,
      setCurrentFilters,
      fetchList,
    });
  };

  const clearFilters = async () => {
    setPage(1);
    await clearEstablishmentFieldRequestFiltersHandler({
      setValue,
      getValues,
      setFiltersApplied,
      setOpenFilters,
      submitFilters,
    });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchList({ ...currentFilters, "P.Pagina": nextPage });
  };

  const handleDetail = (id: number) => {
    navigate(AppRoutes.ESTABLISHMENT_FIELD_REQUEST_DETAIL.replace(":id", String(id)));
  };

  const getDetailHref = (id: number) =>
    AppRoutes.ESTABLISHMENT_FIELD_REQUEST_DETAIL.replace(":id", String(id));

  const getActionElements = (item: EstablishmentFieldRequestItem) => (
    <EstablishmentFieldRequestActions handleDetail={handleDetail} item={item} href={getDetailHref(item.id)} />
  );

  const values = useWatch({ control });
  const filtersEmpty = Object.values(values ?? {}).every((v) => isEmpty(v));

  const tableItems = list?.listado?.map((el) => ({
    ...el,
    displayFecha: el.fecha ? dayjs(el.fecha).format("DD/MM/YYYY") : "-",
    displayEstablecimientoLote:
      el.idTipoSolicitud === RequestTypes.SolicitudLote
        ? `${el.nombreLote} - ${el.establecimiento}`
        : el.establecimiento,
    displayEstado: (
      <RequestStatusBadge status={el.estado as EstablishmentFieldRequestStatus} />
    ),
  })) ?? [];

  usePolling({
    callback: () => fetchList({ ...currentFilters, "P.Pagina": page }, false),
    delay: 15000,
    isActive
  })

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Solicitudes de Establecimientos y Lotes"
        loading={loading}
        showButtons={false}
      />

      <EstablishmentFieldRequestFilter
        control={control}
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        filtersApplied={filtersApplied}
        disableApply={filtersEmpty}
        disableFilters={loading}
        maxDate={dayjs()}
      />

      <Flex flexDir="column">
        {Boolean(!loading && !list?.listado?.length) && <NoData />}

        {Boolean(!loading && list?.listado?.length) && (
          <>
            <StandardTable
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={tableItems}
              headers={establishmentFieldRequestsHeaderElements}
              actionElements={getActionElements}
            />

            {Number(list?.totalPages) > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={list?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
